import React, { useEffect } from 'react';
import { Drawer, Form, Input, InputNumber, Select, Button, message, Tag, Row, Col, Checkbox, Space } from 'antd';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { medicineService, Medicine, CreateMedicineRequest, AuxiliaryData } from '../../services/medicineService';

interface MedicineModalProps {
  visible: boolean;
  medicine?: Medicine;
  onCancel: () => void;
  onSuccess: () => void;
}

const MedicineModal: React.FC<MedicineModalProps> = ({
  visible,
  medicine,
  onCancel,
  onSuccess,
}) => {
  const isEditing = !!medicine;
  const title = isEditing ? 'Cập nhật sản phẩm' : 'Thêm sản phẩm mới';
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { TextArea } = Input;
  const { Option } = Select;

  // Fetch auxiliary data from master tables
  const { data: auxiliaryData } = useQuery<AuxiliaryData>('auxiliary-data', medicineService.getAuxiliaryData);
  const categories = auxiliaryData?.categories || [];
  const units = auxiliaryData?.units || [];
  const dosageForms = auxiliaryData?.dosageForms || [];

  // Mutations
  const createMutation = useMutation(medicineService.createMedicine, {
    onSuccess: () => {
      message.success('Thêm thuốc thành công!');
      queryClient.invalidateQueries('medicines');
      onSuccess?.();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Có lỗi xảy ra khi thêm thuốc');
    },
  });

  const updateMutation = useMutation(medicineService.updateMedicine, {
    onSuccess: () => {
      message.success('Cập nhật thuốc thành công!');
      queryClient.invalidateQueries('medicines');
      onSuccess?.();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Có lỗi xảy ra khi cập nhật thuốc');
    },
  });

  // Set form values when editing
  useEffect(() => {
    if (visible) {
      if (medicine) {
        form.setFieldsValue({
          name: medicine.name,
          code: medicine.code,
          active_ingredient: medicine.active_ingredient,
          dosage_form: medicine.dosage_form,
          strength: medicine.strength,
          manufacturer: medicine.manufacturer,
          registration_number: medicine.registration_number,
          unit: medicine.unit,
          min_stock_level: medicine.min_stock_level,
          max_stock_level: medicine.max_stock_level,
          category: medicine.category,
          description: medicine.description,
          exclude_from_revenue: medicine.exclude_from_revenue || false,
        });
      } else {
        form.resetFields();
      }
    }
  }, [visible, medicine, form]);

  const onFinish = (values: CreateMedicineRequest) => {
    if (!values.name || !values.code || !values.dosage_form || !values.unit || !values.category) {
      message.error('Vui lòng điền đầy đủ thông tin bắt buộc');
      return;
    }

    const requestData = {
      ...values,
      min_stock_level: values.min_stock_level || 0,
      max_stock_level: values.max_stock_level || 1000,
      description: values.description || ''
    };

    if (isEditing && medicine) {
      updateMutation.mutate({ ...requestData, id: medicine.id });
    } else {
      createMutation.mutate(requestData);
    }
  };

  const isLoading = createMutation.isLoading || updateMutation.isLoading;

  return (
    <Drawer
      title={title}
      open={visible}
      onClose={onCancel}
      width={1200}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onCancel}>
            Hủy
          </Button>
          <Button type="primary" loading={isLoading} onClick={() => form.submit()}>
            {isEditing ? 'Cập nhật' : 'Thêm thuốc'}
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          min_stock_level: 0,
          max_stock_level: 1000,
          exclude_from_revenue: false,
        }}
        style={{ width: '100%' }}
        size="middle"
      >
        <Row gutter={[16, 8]}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Tên sản phẩm"
              rules={[{ required: true, message: 'Vui lòng nhập tên sản phẩm!' }]}
            >
              <Input placeholder="Nhập tên sản phẩm" />
            </Form.Item>
            <Form.Item
              name="code"
              label="Mã sản phẩm"
              rules={[{ required: true, message: 'Vui lòng nhập mã sản phẩm!' }]}
            >
              <Input placeholder="Nhập mã sản phẩm" />
            </Form.Item>
            <Form.Item
              name="active_ingredient"
              label="Hoạt chất"
              rules={[{ required: true, message: 'Vui lòng nhập hoạt chất!' }]}
            >
              <Input placeholder="Nhập hoạt chất" />
            </Form.Item>
            <Form.Item
              name="dosage_form"
              label="Dạng bào chế"
              rules={[{ required: true, message: 'Vui lòng chọn dạng bào chế!' }]}
            >
              <Select placeholder="Chọn dạng bào chế" showSearch>
                {dosageForms.map(form => (
                  <Option key={form.id} value={form.name}>{form.name}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="unit"
              label="Đơn vị"
              rules={[{ required: true, message: 'Vui lòng chọn đơn vị!' }]}
            >
              <Select placeholder="Chọn đơn vị" showSearch>
                {units.map(unit => (
                  <Option key={unit.id} value={unit.name}>{unit.name}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="category"
              label="Danh mục"
              rules={[{ required: true, message: 'Vui lòng chọn danh mục!' }]}
            >
              <Select placeholder="Chọn danh mục" showSearch>
                {categories.map(category => (
                  <Option key={category.id} value={category.name}>
                    <Tag color={category.color}>{category.name}</Tag>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="strength"
              label="Hàm lượng"
              rules={[{ required: true, message: 'Vui lòng nhập hàm lượng!' }]}
            >
              <Input placeholder="Ví dụ: 500mg, 10ml" />
            </Form.Item>
            <Form.Item
              name="manufacturer"
              label="Nhà sản xuất"
              rules={[{ required: true, message: 'Vui lòng nhập nhà sản xuất!' }]}
            >
              <Input placeholder="Nhập nhà sản xuất" />
            </Form.Item>
            <Form.Item
              name="registration_number"
              label="Số đăng ký"
              rules={[{ required: true, message: 'Vui lòng nhập số đăng ký!' }]}
            >
              <Input placeholder="Nhập số đăng ký" />
            </Form.Item>
            <Form.Item
              name="min_stock_level"
              label="Tồn kho tối thiểu"
              rules={[{ required: true, message: 'Vui lòng nhập tồn kho tối thiểu!' }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} placeholder="Nhập số lượng tối thiểu" />
            </Form.Item>
            <Form.Item
              name="max_stock_level"
              label="Tồn kho tối đa"
              rules={[{ required: true, message: 'Vui lòng nhập tồn kho tối đa!' }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} placeholder="Nhập số lượng tối đa" />
            </Form.Item>
            <Form.Item name="description" label="Mô tả">
              <TextArea rows={2} placeholder="Nhập mô tả chi tiết về thuốc (không bắt buộc)" />
            </Form.Item>
            <Form.Item name="exclude_from_revenue" valuePropName="checked">
              <Checkbox>
                <span style={{ fontWeight: '500', color: '#dc2626' }}>Không tính doanh thu</span>
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Drawer>
  );
};

export default MedicineModal; 